import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, RefreshCw, Eye, BookOpen, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const STATUS_BADGE = {
  POSTED: { variant: 'default', cls: 'bg-emerald-100 text-emerald-700' },
  REVERSED: { variant: 'secondary', cls: 'bg-amber-100 text-amber-800' },
  DRAFT: { variant: 'outline', cls: '' },
};

const JournalVoucherList = () => {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${backendUrl}/api/fi/journal-vouchers`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load journal vouchers');
    } finally { setLoading(false); }
  };

  const filtered = rows.filter((r) => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.document_number?.toLowerCase().includes(q) ||
      r.reference?.toLowerCase().includes(q) ||
      r.header_text?.toLowerCase().includes(q) ||
      r.document_type_id?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="jv-list-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-orange-600" />
              Journal Vouchers (FI Postings)
            </h1>
            <p className="text-gray-600 mt-1">
              FB50-style manual G/L postings — balanced, auto-numbered, reversal-aware.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} data-testid="jv-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
            <Button onClick={() => navigate('/finance/journal-vouchers/create')} data-testid="jv-new-btn">
              <Plus className="w-4 h-4 mr-1" />New Posting
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="pt-4 grid grid-cols-3 gap-3">
            <div className="relative col-span-2">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                placeholder="Search by document #, reference, type, header text…"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="jv-search-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="jv-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="POSTED">Posted</SelectItem>
                <SelectItem value="REVERSED">Reversed</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{filtered.length} Posting(s)</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No postings yet — click <strong>New Posting</strong> to create one.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document #</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>FY</TableHead>
                    <TableHead>Posting Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Curr</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const badge = STATUS_BADGE[r.status] || STATUS_BADGE.DRAFT;
                    return (
                      <TableRow
                        key={`${r.document_number}-${r.company_code_id}-${r.fiscal_year}`}
                        data-testid={`jv-row-${r.document_number}`}
                      >
                        <TableCell className="font-mono">{r.document_number}</TableCell>
                        <TableCell><Badge variant="outline">{r.document_type_id}</Badge></TableCell>
                        <TableCell>{r.company_code_id}</TableCell>
                        <TableCell>{r.fiscal_year}</TableCell>
                        <TableCell>{r.posting_date}</TableCell>
                        <TableCell className="text-xs">{r.reference || '-'}</TableCell>
                        <TableCell className="text-right font-mono">
                          {Number(r.total_debit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>{r.currency}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
                            {r.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(
                              `/finance/journal-vouchers/${r.document_number}/${r.company_code_id}/${r.fiscal_year}`
                            )}
                            data-testid={`jv-view-${r.document_number}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default JournalVoucherList;
